import ReactGA from "react-ga4";

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

let initialized = false;

// GA4 auto-derives geolocation (city/country/region) from IP for every hit,
// logged-in or not — no custom IP lookup is needed for that.
export const initGA = () => {
  if (initialized || !GA_MEASUREMENT_ID) return;
  // send_page_view is off because this is an SPA: AnalyticsTracker sends
  // page_view manually on every route change. Leaving it on double-counts
  // the very first pageview (once from gtag's implicit config-time hit,
  // once from our own trackPageView call on mount).
  ReactGA.initialize(GA_MEASUREMENT_ID, {
    gtagOptions: { send_page_view: false },
  });
  initialized = true;
};

const getPrimaryRole = (profile) => {
  const roles = (profile?.roles || []).map((r) => r.name);
  if (roles.includes("ADMIN")) return "ADMIN";
  if (roles.includes("MENTOR")) return "MENTOR";
  if (roles.includes("DEVELOPER")) return "DEVELOPER";
  return roles[0] || "UNKNOWN";
};

// GA4 disallows PII (email/name) in events or user properties, so only the
// internal user id and role are sent — never email or full name.
export const identifyUser = (profile) => {
  if (!initialized || !profile?.id) return;
  ReactGA.set({ user_id: String(profile.id) });
  ReactGA.gtag("set", "user_properties", {
    user_role: getPrimaryRole(profile),
  });
};

export const clearIdentity = () => {
  if (!initialized) return;
  ReactGA.set({ user_id: undefined });
  ReactGA.gtag("set", "user_properties", { user_role: undefined });
};

export const trackPageView = (path, title) => {
  if (!initialized) return;
  ReactGA.send({ hitType: "pageview", page: path, title });
};

// Explicit time-on-page event, sent when navigating away from `path`.
export const trackPageEngagement = (path, engagementTimeMs) => {
  if (!initialized || !engagementTimeMs || engagementTimeMs < 250) return;
  ReactGA.event("page_engagement_time", {
    page_path: path,
    engagement_time_msec: Math.round(engagementTimeMs),
  });
};

// Generic helper for instrumenting journeys/flows/actions across the app.
export const trackEvent = (action, params = {}) => {
  if (!initialized) return;
  ReactGA.event(action, params);
};

// Dwell-time helper for any content unit (chapter, tab, etc.) — mirrors
// trackPageEngagement but lets the caller pick the event name/params.
export const trackEngagement = (eventName, params, engagementTimeMs) => {
  if (!initialized || !engagementTimeMs || engagementTimeMs < 250) return;
  ReactGA.event(eventName, {
    ...params,
    engagement_time_msec: Math.round(engagementTimeMs),
  });
};

// Single consolidated event for all admin CRUD/review actions, keyed by
// entity + action, so GA doesn't accumulate dozens of one-off event names.
export const trackAdminAction = (entity, action, details = {}) => {
  trackEvent("admin_action", { entity, action, ...details });
};
